import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'tb-upload-errors',
    category: 'troubleshooting',
    title: 'Perché il mio caricamento è fallito? — messaggi di errore spiegati',
    summary: 'Ogni errore in rosso e avviso in giallo, con la causa e la soluzione.',
    body: (
      <>
        <p>
          Quando un caricamento viene rifiutato, viene mostrato un banner <strong>rosso</strong> e un
          elenco di problemi. I messaggi in <strong>giallo</strong> sono solo avvisi — il file viene
          comunque accettato. Trova il tuo messaggio qui sotto.
        </p>

        <h5 className="mt-3">Errori rossi (file rifiutato)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Messaggio</th><th>Cosa significa &amp; come correggerlo</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><Code>Missing required column: "..."</Code></td>
              <td>La riga delle intestazioni non contiene una colonna richiesta dal file — di solito
                una differenza di ortografia o maiuscole. Le intestazioni devono corrispondere
                esattamente, ad es. <Code>consumption_kwh</Code>, tutto in minuscolo. Correggi la
                prima riga e ricarica il file.</td>
            </tr>
            <tr>
              <td><Code>Value must be a number, got: "..."</Code></td>
              <td>Una cella che dovrebbe contenere un numero contiene qualcos'altro — quasi sempre una
                <strong> virgola decimale</strong> (<Code>3,5</Code>); deve essere un punto (<Code>3.5</Code>).
                Per convertire un intero file in una volta sola, consulta <ArticleLink to="data-fix-commas">Correggere
                un file che usa le virgole per i decimali</ArticleLink>. Rimuovi anche unità di misura,
                spazi o altro testo dalle celle numeriche.</td>
            </tr>
            <tr>
              <td><Code>Value must be non-negative, got: ...</Code></td>
              <td>Un valore di consumo, generazione o prezzo è negativo. Questi non possono essere
                inferiori a zero — un numero negativo di solito indica che due colonne sono state
                scambiate.</td>
            </tr>
            <tr>
              <td><Code>Invalid timestamp format: "..."</Code></td>
              <td>Una cella data/ora non è leggibile. Usa <Code>2025-01-01T14:00:00Z</Code>.
                Consulta <ArticleLink to="data-csv-basics">Creazione corretta di file CSV</ArticleLink> per
                i formati di data accettati e una formula che corregge un'intera colonna in una volta.</td>
            </tr>
            <tr>
              <td><Code>CSV file contains no data rows</Code></td>
              <td>Il file ha solo una riga di intestazione o è vuoto. Aggiungi le righe di dati orari.</td>
            </tr>
            <tr>
              <td><Code>period_start must be before period_end</Code></td>
              <td>(File bollette energetiche) La data di inizio di una bolletta è uguale o successiva
                alla data di fine. Correggi le date.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-4">Avvisi gialli (file ancora accettato)</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Messaggio</th><th>Cosa significa</th></tr>
          </thead>
          <tbody>
            <tr><td><Code>Extra columns found and will be ignored</Code></td><td>Il tuo file contiene colonne che la piattaforma non utilizza. Non è un problema — vengono semplicemente ignorate.</td></tr>
            <tr><td><Code>... gap(s) detected in the time series</Code></td><td>Alcune ore mancano tra la prima e l'ultima riga. Aggiungi le ore mancanti per la massima precisione.</td></tr>
            <tr><td><Code>timestamp(s) are not in chronological order</Code></td><td>Le righe non sono ordinate per data/ora. Ordina il file per la colonna del timestamp.</td></tr>
            <tr><td><Code>has ... missing value(s)</Code></td><td>Alcune celle in una colonna numerica sono vuote. Compilale (usa <Code>0</Code> dove appropriato).</td></tr>
            <tr><td><Code>... potential outliers</Code></td><td>Alcuni valori sono insolitamente alti o bassi. Verificali nel caso si tratti di un errore di digitazione, altrimenti ignorali.</td></tr>
            <tr><td><Code>contains all zero values</Code></td><td>Un'intera colonna è a zero. È accettabile per il solare notturno; altrimenti verifica di aver esportato i dati corretti.</td></tr>
          </tbody>
        </Table>
        <Tip>
          La maggior parte degli errori rossi deriva dalla virgola decimale o da intestazioni
          digitate in modo errato. Partire da un modello scaricato evita entrambi i problemi —
          consulta <em>«Creazione corretta di file CSV»</em>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-forecast-flat',
    category: 'troubleshooting',
    title: 'La mia previsione appare piatta o errata',
    summary: 'Quando la linea prevista è piatta, una copia di ieri, o completamente sbagliata — cosa controllare.',
    body: (
      <>
        <p>Se una previsione sembra inutile, quasi sempre è in gioco una di queste cause.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Cosa vedi</th><th>Causa più probabile</th><th>Cosa fare</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Linea piatta</strong> — la previsione è un singolo valore per tutto l'orizzonte temporale.</td>
              <td>Storico di input molto ridotto, oppure l'input è per lo più zero o un singolo valore ripetuto.</td>
              <td>Carica più storico (almeno un mese, idealmente un anno). Verifica che il file non contenga un singolo valore ripetuto.</td>
            </tr>
            <tr>
              <td><strong>«Copia di ieri»</strong> — la previsione sembra le ultime 24 ore, spostate in avanti.</td>
              <td>Il modello è ricorso a una baseline ingenua perché i dati sono troppo brevi per addestrare un modello reale.</td>
              <td>Aggiungi più storico. Aggiungi un file meteo per le previsioni solari (vedi <ArticleLink to="data-weather">File meteo</ArticleLink>).</td>
            </tr>
            <tr>
              <td><strong>Picchi / valori negativi</strong> nella previsione.</td>
              <td>Valori anomali nello storico (es. un malfunzionamento del contatore) stanno distorcendo il modello.</td>
              <td>Apri il file storico, trova la riga sospetta, correggila o rimuovila, ricarica e riesegui.</td>
            </tr>
            <tr>
              <td><strong>Banda di confidenza molto ampia</strong>.</td>
              <td>Il modello sa di essere incerto — di solito storico breve o dati recenti insoliti.</td>
              <td>Aggiungi più dati; la banda dovrebbe restringersi alla prossima esecuzione.</td>
            </tr>
            <tr>
              <td><strong>Avviso «dati di esempio» sulla pagina</strong>.</td>
              <td>Il servizio di previsione non era disponibile o i dati erano insufficienti — vengono mostrati risultati illustrativi.</td>
              <td>Controlla la pagina di caricamento per file mancanti; riprova tra qualche minuto.</td>
            </tr>
            <tr>
              <td><strong>La previsione solare è approssimativamente corretta ma la scala è sbagliata</strong>.</td>
              <td>La capacità FV / l'orientamento dei pannelli potrebbero essere impostati in modo errato sull'asset, oppure il file di generazione usa kWh dove è atteso kW (o viceversa).</td>
              <td>Verifica la capacità installata dell'asset (kWp). Verifica che l'unità del file di generazione corrisponda a quella attesa dalla piattaforma nella pagina di caricamento.</td>
            </tr>
          </tbody>
        </Table>

        <Tip>
          Un controllo utile: se prevedi il giorno <em>passato</em> di un edificio e la previsione
          non assomiglia per niente a quello che è accaduto, il modello non ha dati sufficienti.
          Aggiungere storico è quasi sempre più efficace che modificare le impostazioni.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-optimisation-infeasible',
    category: 'troubleshooting',
    title: 'L\'ottimizzazione indica «infeasible»',
    summary: 'Quando l\'esecuzione di Ottimizzazione non riesce a trovare alcun programma valido — cause comuni e soluzioni.',
    body: (
      <>
        <p>
          «Infeasible» significa che l'ottimizzatore non è riuscito a trovare <em>nessun</em> programma
          che soddisfi tutti i vincoli contemporaneamente. Di solito un vincolo è troppo restrittivo.
        </p>

        <h5 className="mt-3">Cose da verificare, in ordine</h5>
        <ol>
          <li>
            <strong>Capacità della batteria rispetto alla domanda.</strong> Se hai forzato la
            batteria a coprire il picco serale ma la sua capacità è troppo piccola, l'ottimizzatore
            non ha un piano valido. Aumenta la capacità della batteria o allenta il vincolo.
          </li>
          <li>
            <strong>Finestra di ricarica EV rispetto all'energia richiesta.</strong> Se un EV ha
            bisogno di 40 kWh ma è collegato solo per 2 ore a 7 kW (=14 kWh massimi), non può
            essere ricaricato completamente. Amplia la finestra o riduci il SOC richiesto.
          </li>
          <li>
            <strong>Obiettivo di autosufficienza rispetto alla generazione.</strong> Un obiettivo
            di «100% autosufficiente» con poco FV e nessuna importazione dalla rete consentita è
            impossibile. Abbassa l'obiettivo o consenti alcune importazioni.
          </li>
          <li>
            <strong>Limiti SOC min/max.</strong> Se il SOC minimo è superiore al SOC massimo (o
            entrambi bloccati allo stesso livello), non c'è spazio per operare. Controlla entrambi
            i limiti.
          </li>
          <li>
            <strong>Prezzi tariffari negativi.</strong> Alcune esportazioni mostrano prezzi
            all'ingrosso negativi nei mercati reali. Se imposti una regola «esportazione ≥ 0»,
            quelle ore diventano infeasible. Rimuovi la regola o pre-filtra il file tariffario.
          </li>
          <li>
            <strong>Dati mancanti nell'orizzonte.</strong> Un'interruzione nel file di carico o
            tariffario all'interno dell'orizzonte di ottimizzazione può bloccare il solver.
            Ricarica il file con il gap colmato (usa il valore dell'ora precedente o 0).
          </li>
        </ol>

        <h5 className="mt-3">Come diagnosticare rapidamente</h5>
        <p>
          Riesegui con l'obiettivo <strong>«Minimizza il costo»</strong> e <em>nessun</em> obiettivo
          rigido di autosufficienza / autoconsumo. Se ha successo, il vincolo vincolante era
          il tuo obiettivo — allentalo. Se continua a fallire, il vincolo vincolante è la capacità
          degli asset o un'interruzione nei dati.
        </p>
        <Tip>
          Dimezzare l'orizzonte (24 h → 12 h) a volte ha successo quando 24 h non funziona. Questo
          indica un blocco temporale specifico in cui si trova il problema — esamina quel blocco
          nei tuoi dati.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-kpi-sanity-check',
    category: 'troubleshooting',
    title: 'I miei KPI sembrano troppo buoni — o troppo negativi',
    summary: 'Quando l\'autosufficienza è al 99%, l\'NPV è enorme o il payback è implausibilmente breve — cosa controllare.',
    body: (
      <>
        <p>
          Un KPI che sembra «troppo bello per essere vero» deriva quasi sempre da un piccolo insieme
          di errori nei dati. Esegui questi controlli prima di condividere il risultato.
        </p>

        <h5 className="mt-3">Se l'autosufficienza o l'autoconsumo è implausibilmente alto</h5>
        <ul>
          <li><strong>File di consumo scalato troppo piccolo.</strong> Valori in kW inseriti in una colonna kWh (o viceversa) fanno sembrare la comunità piccola rispetto al suo FV. Ricontrolla l'unità nella pagina di caricamento.</li>
          <li><strong>Caricato solo un anno parziale.</strong> Se hai caricato solo i mesi estivi, ovviamente il solare copre la maggior parte della domanda. Carica un anno completo per una cifra rappresentativa.</li>
          <li><strong>Edifici mancanti.</strong> Solo uno dei tre edifici è sulla piattaforma, quindi la comunità sembra più piccola e più coperta dal solare rispetto alla realtà.</li>
        </ul>

        <h5 className="mt-3">Se l'NPV o il risparmio annuale è implausibilmente grande</h5>
        <ul>
          <li><strong>Tariffa nelle unità sbagliate.</strong> €/MWh trattato come €/kWh gonfia i risparmi di 1000×. Apri il file tariffario — un prezzo di <Code>120</Code> dovrebbe essere <Code>0.12</Code> se rappresenta €/kWh.</li>
          <li><strong>Durata di vita troppo lunga.</strong> Un orizzonte di 30 anni su una batteria da 15 anni sopravvaluta i risparmi. Adegua la durata di vita all'asset.</li>
          <li><strong>Tasso di sconto impostato a 0%.</strong> Nessuno sconto significa che i risparmi futuri contano pienamente — di solito irrealistico. Usa il 3–6% a meno che il tuo team finanziario non dica diversamente.</li>
          <li><strong>Ipotesi di inflazione troppo aggressiva.</strong> Se hai ipotizzato una crescita annua dei prezzi dell'elettricità dell'8% per sempre, i risparmi esplodono. Usa una cifra prudente (1–4%).</li>
        </ul>

        <h5 className="mt-3">Se i risultati sembrano implausibilmente negativi</h5>
        <ul>
          <li><strong>File tariffario mancante o tutto zero.</strong> Nessun prezzo = nessun risparmio da contare. Carica la tariffa e riesegui.</li>
          <li><strong>Capacità FV lasciata a 0 kWp.</strong> Controlla l'asset nella pagina degli asset della comunità.</li>
          <li><strong>Efficienza della batteria errata.</strong> Un valore predefinito del 50% farebbe «perdere» metà di ogni kWh — controlla l'efficienza round-trip dell'asset.</li>
          <li><strong>File di generazione nella colonna sbagliata.</strong> Se consumo e generazione sono stati scambiati, l'ottimizzatore vede l'edificio come non produttivo.</li>
        </ul>

        <Tip>
          Un controllo utile: dividi il risparmio annuale per la bolletta elettrica annuale della
          comunità. Se il risultato supera il 50–70%, qualcosa è probabilmente sovrastimato. I
          progetti di energia comunitaria nel mondo reale tipicamente riducono le bollette del
          10–40%.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'tb-faq',
    category: 'troubleshooting',
    title: 'Domande frequenti',
    summary: 'Risposte brevi alle domande più comuni.',
    body: (
      <>
        <h5>Ho bisogno di un anno completo di dati?</h5>
        <p>Un anno completo (8.760 righe orarie) fornisce i migliori risultati, ma gli strumenti
          funzionano anche con meno. È richiesto almeno un giorno (24 righe) per i file basati
          sul tempo.</p>

        <h5 className="mt-3">Il mio file usa il punto e virgola ( ; ) tra i valori — è un problema?</h5>
        <p>No. La piattaforma rileva automaticamente virgole, punti e virgola e tabulazioni. Il
          separatore decimale all'interno dei numeri deve comunque essere un punto.</p>

        <h5 className="mt-3">Posso caricare lo stesso tipo di file di nuovo?</h5>
        <p>Sì — caricare di nuovo sostituisce il file precedente per quel tipo (per le tariffe
          Day-Ahead, ogni file giornaliero viene conservato separatamente per data).</p>

        <h5 className="mt-3">I risultati sembrano dati di esempio — perché?</h5>
        <p>Se un servizio di calcolo è temporaneamente non disponibile, o i dati richiesti non
          sono ancora stati caricati, la piattaforma mostra risultati illustrativi chiaramente
          etichettati in modo che tu possa comunque esplorare. Carica dati reali ed esegui
          nuovamente lo strumento per ottenere risultati veri.</p>

        <h5 className="mt-3">Perché un pulsante è disattivato?</h5>
        <p>Alcuni account sono in sola lettura (ruolo visualizzatore) e alcune azioni richiedono
          che i dati siano stati caricati prima. Passa il cursore sul pulsante o controlla i
          suggerimenti della pagina per vedere cosa è necessario.</p>
      </>
    ),
  },
]
